import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type BaseSelectProps,
} from "@mui/material";
import { type Control, Controller, type FieldValues } from "react-hook-form";
import type { FieldPath } from "react-hook-form/dist/types";
import { type ReactElement } from "react";
import { type ISelectObject } from "@/types/select";
import { SizingWrapper, type SizingWrapperStyle } from "@/components/Wrapper";
import ClearIcon from "@mui/icons-material/Clear";

interface IProps<T extends FieldValues> {
  control: Control<T> | undefined;
  name: FieldPath<T>;
  label: string;
  options: ISelectObject[];
  isClearable?: boolean;
  size?: SizingWrapperStyle;
  /** MUI Selectの表示密度。省略時は従来どおり "medium"（既存の見た目を変えない） */
  muiSize?: "small" | "medium";
  disabled?: boolean;
  inputProps?: BaseSelectProps["inputProps"];
  callbackOnChange?: (value: string) => void;
}

/**
 * Selectコンポーネントのコントローラー対応版
 * コントローラーの詳細はReact-Hook-Formのドキュメントを参照
 * https://www.react-hook-form.com/api/usecontroller/controller/
 * @param props
 */
const CSelect = <T extends FieldValues>(props: IProps<T>): ReactElement => {
  const {
    name,
    control,
    label,
    options,
    isClearable,
    size,
    muiSize,
    disabled,
    inputProps,
    callbackOnChange,
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const typedValue = value as string;

        // 選択されたオプションを見つける
        const selectedOption = options.find((opt) => opt.value === typedValue);

        return (
          <SizingWrapper size={size}>
            <FormControl fullWidth size={muiSize}>
              <InputLabel id="select-label">{label}</InputLabel>
              <Select
                id="select-id"
                labelId="select-label"
                label={label}
                size={muiSize}
                value={selectedOption ? JSON.stringify(selectedOption) : ""}
                onChange={(event: SelectChangeEvent<string>): void => {
                  const selectedObject = JSON.parse(
                    event.target.value
                  ) as ISelectObject;
                  onChange(selectedObject.value);
                  if (callbackOnChange) {
                    callbackOnChange(selectedObject.value);
                  }
                }}
                defaultValue=""
                disabled={disabled}
                renderValue={() => selectedOption?.label ?? ""}
                endAdornment={
                  isClearable ? (
                    <IconButton
                      sx={{
                        display:
                          selectedOption?.label === "" ? "none" : "inline-flex",
                        marginRight: "10px",
                      }}
                      onClick={(): void => {
                        onChange("");
                        if (callbackOnChange) {
                          callbackOnChange("");
                        }
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : (
                    <></>
                  )
                }
                inputProps={inputProps}
                error={Boolean(error)}
              >
                {options.map((option, index) => (
                  <MenuItem key={index} value={JSON.stringify(option)}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {error ? (
                <FormHelperText error>{error.message}</FormHelperText>
              ) : null}
            </FormControl>
          </SizingWrapper>
        );
      }}
    />
  );
};

export { CSelect };
